import { useState } from "react";
import { Radar, RefreshCw } from "lucide-react";
import { Button, Loader, cn } from "@wa/ui";
// The scanner page's own components, reused whole. The verdict a company
// deserves does not change because it was opened from the register instead of
// from the scanner, so nothing here is a second implementation of it.
import {
  ContactsPanel,
  FindingsList,
  FreshnessPanel,
  ScanHero,
  ScanRawDialog,
  SeoPanel,
  TechGrid,
  TransportPanel,
  WordPressPanel,
} from "../../site-scanner";
import { useEntityScan } from "./useEntityScan";

type T = (k: string, vars?: Record<string, string | number>) => string;

/**
 * The website scan for one register entity, on the company detail page.
 *
 * The register says a company exists and what it earns; this says whether the
 * website it publishes is worth an offer - which platform, how many releases
 * behind, what is broken and who answers the phone. Between this and the domain
 * card above it, the whole sales picture is on one screen.
 *
 * On demand, like the domain check: opening a company must not fetch that
 * company's homepage. The identity panel the scanner page shows is left out on
 * purpose - the company is the page.
 */
export function SiteScanCard({ regCode, webUrl, t }: { regCode: string; webUrl: string | null; t: T }) {
  const { state, run } = useEntityScan(regCode);
  const [rawOpen, setRawOpen] = useState(false);

  // Nothing to scan - the register holds no website for this company.
  if (!webUrl) return null;

  const scan = state.kind === "done" ? state.scan : null;

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 text-sm font-semibold">
          <Radar className="size-4 text-primary" /> {t("scanner.entity.title")}
        </h2>
        {!scan && (
          <Button variant="outline" size="sm" onClick={() => void run(false)} disabled={state.kind === "busy"}>
            <RefreshCw className={cn("size-3.5", state.kind === "busy" && "animate-spin")} />
            {t(state.kind === "busy" ? "scanner.entity.scanning" : "scanner.entity.scan")}
          </Button>
        )}
      </div>

      {state.kind === "idle" && (
        <p className="rounded-xl border border-dashed border-border bg-card/60 px-4 py-5 text-sm text-muted-foreground">
          {t("scanner.entity.hint", { url: webUrl })}
        </p>
      )}

      {state.kind === "busy" && (
        <div className="flex justify-center py-10">
          <Loader />
        </div>
      )}

      {state.kind === "error" && (
        <p className="rounded-md bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-400">
          {t(state.messageKey)}
        </p>
      )}

      {scan && (
        <>
          <ScanHero
            scan={scan}
            refreshing={false}
            onRefresh={() => void run(true)}
            onRaw={() => setRawOpen(true)}
            t={t}
          />

          <div className="grid gap-4 lg:grid-cols-2">
            <FindingsList findings={scan.findings} t={t} />
            {scan.contacts && (
              <ContactsPanel
                contacts={scan.contacts}
                companyName={scan.identity?.companyName ?? undefined}
                regCode={regCode}
                domain={scan.domain}
                t={t}
              />
            )}
          </div>

          {scan.freshness && <FreshnessPanel freshness={scan.freshness} t={t} />}
          {scan.wordpress && <WordPressPanel wp={scan.wordpress} freshness={scan.freshness?.wp ?? null} t={t} />}

          <TechGrid technologies={scan.technologies} t={t} />

          <div className="grid gap-4 lg:grid-cols-2">
            {scan.seo && <SeoPanel seo={scan.seo} t={t} />}
            <TransportPanel http={scan.http} t={t} />
          </div>

          <ScanRawDialog scan={scan} open={rawOpen} onOpenChange={setRawOpen} t={t} />
        </>
      )}
    </section>
  );
}
